<template>
  <div>
    <Filters
      v-if="showDialog"
      :topics="topicItems"
      :kinds="availableKinds"
      :filter="filter"
      @close="showDialog = false"
      @applyFilters="applyFilters"
    />
    <div class="d-md-flex justify-md-space-between">
      <!-- TODO: check unsupported prop: text -->
      <v-btn
        class="my-2 ma-sm-2 w-xs-full"
        color="primary"
        variant="outlined"
        :disabled="isContentTypeDisabled"
        @click="openFiltersDialog"
      >
        <v-icon
          start
          size="x-small"
        >
          mdi mdi-tune-variant
        </v-icon>

        {{ $t('buttons.content-creator.filters') }}
        <v-badge
          v-if="filter.numberOfAppliedFilters"
          color="red"
          :content="filter.numberOfAppliedFilters"
          offset-y="-16"
          offset-x="-16"
        />
      </v-btn>
      <div class="justify-space-between d-none d-sm-flex">
        <Tabs
          :section="section"
          :disabled="isContentTypeDisabled"
          @sectionChange="onSectionChange($event)"
          @refetch="refetchSavedContents"
        />
      </div>
    </div>
    <v-divider class="divider" />

    <info-box
      v-if="!isContentTypeDisabled && contents.length === 0 && !isGenerating"
      :info-message="infoMessage"
    />
    <v-window
      v-model="section"
      disabled
    >
      <SavedContentTab
        v-for="filterValue in filterValues"
        :key="filterValue"
        :content-type="contentType"
        :disabled="isContentTypeDisabled"
        :filter-value="filterValue"
        :saved-contents="contents"
        @contentSaved="refetchSavedContents"
        @savedScheduledContent="refetchSavedContents"
      />
    </v-window>

    <div
      v-if="contents.length > 0"
      class="text-center mt-10 mb-5"
    >
      <v-pagination
        v-model="currentPage"
        :disabled="disabled"
        :length="paginationLength"
        :total-visible="7"
        prev-icon="mdi-menu-left"
        next-icon="mdi-menu-right"
        @update:modelValueeslint="updatePage"
      />
    </div>

    <ProgressCircular v-if="isGenerating" />
  </div>
</template>

<script>
import InfoBox from '@/components/InfoBox.vue'
import ProgressCircular from '@/components/ProgressCircular.vue'
import { transformToKebabCase as kebabCase } from '@/lib/kebabCase'
import { textTruncate } from '@/lib/truncate'
import feature from '@/mixins/feature'
import { ContentType } from '@/modules/contentCreator/creator/enums/ContentType'
import { FilterType } from '@/modules/contentCreator/creator/enums/FilterType'
import KindType, { KindTypeIcon } from '@/modules/contentCreator/creator/enums/KindType'
import Filters from '@/modules/contentCreator/creator/Filters.vue'
import GET_SAVED_CONTENTS from '@/modules/contentCreator/creator/queries/getSavedContent.gql'
import GET_SCHEDULED_CONTENTS from '@/modules/contentCreator/creator/queries/getScheduledContents.gql'
import SavedContentTab from '@/modules/contentCreator/creator/SavedContentsTab.vue'
import { buildContentsTimelineInput } from '@/modules/contentCreator/creator/scheduledContents'
import Tabs from '@/modules/contentCreator/creator/Tabs.vue'

import GET_ACTIVE_TOPICS from './queries/getActiveTopics.gql'

const TIMELINE_SECTIONS = [
  FilterType.ALL,
  FilterType.TODAY,
  FilterType.WEEK,
  FilterType.MONTH,
  FilterType.YEAR,
]

export default {
  components: { Tabs, SavedContentTab, ProgressCircular, Filters, InfoBox },
  mixins: [feature],
  props: {
    updateSavedContents: {
      type: Number,
      default: () => 0,
    },
    contentType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      filter: {
        selectedKinds: [],
        selectedTopics: [],
        numberOfAppliedFilters: 0,
      },
      savedContents: {
        total: 0,
        savedContents: [],
      },
      scheduledContents: {
        total: 0,
        scheduledContents: [],
      },
      section: TIMELINE_SECTIONS.includes(this.$route.query['timeline-section'])
        ? this.$route.query['timeline-section']
        : FilterType.ALL,
      windowIndex: 0,
      showDialog: false,
      currentPage: 1,
    }
  },
  computed: {
    filterValues() {
      return [
        this.FilterType.ALL,
        this.FilterType.TODAY,
        this.FilterType.WEEK,
        this.FilterType.MONTH,
        this.FilterType.YEAR,
      ]
    },
    paginationLength() {
      const total =
        this.contentType === this.ContentType.SAVED_CONTENT
          ? this.savedContents.total
          : this.scheduledContents.total
      return Math.ceil(total / 20)
    },
    infoMessage() {
      return this.contentType === this.ContentType.SAVED_CONTENT
        ? this.$t('alerts.content-creator.save-content.info')
        : this.$t('alerts.content-creator.scheduled-content.info')
    },
    contents() {
      return this.contentType === ContentType.SAVED_CONTENT
        ? this.savedContents.savedContents
        : this.scheduledContents.scheduledContents
    },
    ContentType() {
      return ContentType
    },
    FilterType() {
      return FilterType
    },
    isMasterView() {
      return this.$route.path.startsWith('/master')
    },
    availableKinds() {
      return Object.keys(KindType).map((key) => {
        return {
          value: key,
          text: this.$t(`content-creator.text-kind-types.${this.kebabCase(key)}`),
          icon: KindTypeIcon[key],
        }
      })
    },
    isGenerating() {
      return this.$apollo.loading
    },
    topicItems() {
      return this.topics
        .map((topic) => {
          return {
            value: topic.id,
            text: topic.name,
            type: topic.landingpage ? topic.landingpage.type : 'TOPIC',
          }
        })
        .reduce(
          (acc, { type, ...rest }) => {
            if (type in acc) {
              acc[type].items.push(rest)
            } else {
              acc.TOOL.items.push({ type, ...rest })
            }
            return acc
          },
          {
            TOPIC: { items: [], header: 'Topics' },
            TOOL: { items: [], header: 'Tools' },
            GUIDE: { items: [], header: 'Ratgeber' },
            CHECKLIST: { items: [], header: 'Checklisten' },
          }
        )
    },
    isContentTypeDisabled() {
      const featureConfig = this.getFeature(this.featureNames.CONTENT_CREATOR)?.config
      if (!featureConfig) return true
      const options = {
        [this.ContentType.SAVED_CONTENT]: featureConfig.isHistoryEnabled,
        [this.ContentType.SCHEDULED_CONTENT]: featureConfig.schedulePost,
      }
      return !options[this.contentType]
    },
  },
  watch: {
    updateSavedContents() {
      this.refetchSavedContents()
    },
    '$route.query.timeline-section': {
      handler(value) {
        this.section = TIMELINE_SECTIONS.includes(value) ? value : FilterType.ALL
      },
    },
    section: {
      immediate: true,
      handler(newValue) {
        this.windowIndex = this.getSectionIndexMap(newValue)

        if (this.$route.query['timeline-section'] === newValue) return

        this.$router.replace({
          query: { ...this.$route.query, 'timeline-section': newValue },
        })
      },
    },
  },
  methods: {
    kebabCase,
    getSectionIndexMap(filterType) {
      const sectionIndexMap = {
        [FilterType.ALL]: 0,
        [FilterType.TODAY]: 1,
        [FilterType.WEEK]: 2,
        [FilterType.MONTH]: 3,
        [FilterType.YEAR]: 4,
      }
      return sectionIndexMap[filterType] || 0
    },
    getIndexSectionMap(index) {
      const indexSectionMap = {
        0: FilterType.ALL,
        1: FilterType.TODAY,
        2: FilterType.WEEK,
        3: FilterType.MONTH,
        4: FilterType.YEAR,
      }
      return indexSectionMap[index]
    },
    handleWindowChange(index) {
      const mappedSection = this.getIndexSectionMap(index)
      if (mappedSection && mappedSection !== this.section) {
        this.onSectionChange(mappedSection)
      }
      this.windowIndex = index
    },
    onSectionChange(value) {
      if (TIMELINE_SECTIONS.includes(value)) {
        this.section = value
      }
    },
    refetchSavedContents() {
      this.resetCurrentPage()

      if (this.contentType === ContentType.SAVED_CONTENT) {
        this.$apollo.queries.savedContents.refetch()
      } else {
        this.$apollo.queries.scheduledContents.refetch()
      }
    },
    resetCurrentPage() {
      this.currentPage = 1
    },
    updatePage(currentPage) {
      this.currentPage = currentPage
    },
    textTruncate,
    openFiltersDialog() {
      this.showDialog = true
    },
    applyFilters(filter) {
      this.filter = filter
    },
    contentsTimelineInput() {
      return buildContentsTimelineInput({
        timeRange: this.section,
        textKinds: this.filter.selectedKinds,
        topics: this.filter.selectedTopics,
        page: this.isContentTypeDisabled ? 1 : this.currentPage,
        perPage: this.isContentTypeDisabled ? 3 : undefined,
        isMasterView: this.isMasterView,
      })
    },
  },
  apollo: {
    topics: {
      query: GET_ACTIVE_TOPICS,
    },
    savedContents: {
      query: GET_SAVED_CONTENTS,
      fetchPolicy: 'no-cache',
      variables() {
        return { input: this.contentsTimelineInput() }
      },
      skip() {
        return this.contentType === ContentType.SCHEDULED_CONTENT
      },
    },
    scheduledContents: {
      query: GET_SCHEDULED_CONTENTS,
      fetchPolicy: 'no-cache',
      variables() {
        return { input: this.contentsTimelineInput() }
      },
      skip() {
        return this.contentType === ContentType.SAVED_CONTENT
      },
    },
  },
}
</script>

<style scoped>
.divider {
  border-color: #f1f1f4;
}
:deep(.v-timeline .v-timeline-divider__dot) {
  background: rgb(var(--v-theme-primary));
}
:deep(.v-timeline .v-timeline-divider__inner-dot) {
  background: transparent;
}
</style>
